import { splitSearchWords } from 'common/utils/string.utils';

export type TSearchHighlightChunk = string | { text: string };

type TMatchedTextChunk = {
  highlighted: boolean;
  text: string;
};

// Queries are user-controlled, so regex characters such as "+" must be matched literally.
const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeSearchTerms = (searchTerms: string[]): string[] => {
  const uniqueSearchTerms = new Map<string, string>();

  for (const searchTerm of searchTerms) {
    const normalizedSearchTerm = searchTerm.toLowerCase();
    if (!uniqueSearchTerms.has(normalizedSearchTerm)) uniqueSearchTerms.set(normalizedSearchTerm, searchTerm);
  }

  // Prefer longer terms so a prefix such as "an" cannot consume the start of "anna" before "anna" is tried.
  return [...uniqueSearchTerms.values()].sort((left, right) => right.length - left.length);
};

const matchSearchTerms = (text: string, searchTerms: string[]): TMatchedTextChunk[] => {
  const searchTermPattern = searchTerms.map(escapeRegex).join('|');

  const regex = new RegExp(`(${searchTermPattern})`, 'gi');

  return text.split(regex).flatMap((chunk, index) => {
    if (!chunk) return [];
    return [{ highlighted: index % 2 === 1, text: chunk }];
  });
};

const mergeConsecutiveHighlights = (chunks: TMatchedTextChunk[]): TMatchedTextChunk[] => {
  const mergedChunks: TMatchedTextChunk[] = [];

  for (let index = 0; index < chunks.length; index++) {
    const chunk = chunks[index];
    const previousChunk: TMatchedTextChunk | undefined =
      mergedChunks.length > 0 ? mergedChunks[mergedChunks.length - 1] : undefined;
    const nextChunk: TMatchedTextChunk | undefined = index + 1 < chunks.length ? chunks[index + 1] : undefined;

    // Adjacent matched fragments such as "Jo" + "hn" form one visual highlight.
    if (chunk.highlighted && previousChunk?.highlighted) {
      previousChunk.text += chunk.text;
      continue;
    }

    // Whitespace between two matches is included in the same mark, so both "John Doe" and "Doe John"
    // render continuously. Non-whitespace separators deliberately keep the highlights independent.
    if (!chunk.highlighted && !chunk.text.trim() && previousChunk?.highlighted && nextChunk?.highlighted) {
      previousChunk.text += chunk.text + nextChunk.text;
      index++;
      continue;
    }

    mergedChunks.push({ ...chunk });
  }

  return mergedChunks;
};

const toSearchHighlightChunks = (chunks: TMatchedTextChunk[]): TSearchHighlightChunk[] => {
  return chunks.map((chunk) => (chunk.highlighted ? { text: chunk.text } : chunk.text));
};

export const getSearchHighlightChunks = (params: {
  matchSearchTermsIndependently?: boolean;
  searchQuery?: string;
  text: string;
}): TSearchHighlightChunk[] => {
  const { matchSearchTermsIndependently, searchQuery, text } = params;
  if (!searchQuery || !text) return [text];

  // Phrase mode treats the complete query as one literal value. Independent mode uses the same whitespace
  // tokenization as filtering, allowing each query term to match separately in any displayed field.
  const parsedSearchTerms = matchSearchTermsIndependently ? splitSearchWords(searchQuery) : [searchQuery];
  const searchTerms = normalizeSearchTerms(parsedSearchTerms);
  if (searchTerms.length === 0) return [text];

  const matchedChunks = matchSearchTerms(text, searchTerms);

  // Merge multi-term queries such as "Jo hn" into one continuous highlight; single-term matches stay separate.
  const shouldMergeHighlights = matchSearchTermsIndependently && searchTerms.length > 1;
  const highlightChunks = shouldMergeHighlights ? mergeConsecutiveHighlights(matchedChunks) : matchedChunks;
  return toSearchHighlightChunks(highlightChunks);
};
